import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, MapPin, Building2, DollarSign, Calendar, CheckCircle2, Download } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ContactFormPopup from '../components/ContactFormPopup';

const ProjectDetail = () => {
  const { projectId } = useParams();
  const [showPopup, setShowPopup] = React.useState(false);
  const [popupType, setPopupType] = React.useState('general');

  const projectDetails = {
    'taiyo-residences': {
      name: 'Taiyo Residences',
      developer: 'LMD',
      location: 'Wasl Gate',
      hero: 'https://res.cloudinary.com/dqfcgy1vv/image/upload/v1776776608/Taiyo_Residences_ycqbg3.png',
      price: 'Starting from AED 1,000,000',
      completion: 'Q1 2028',
      description: 'Taiyo Residences at Wasl Gate is where light, design, and smart investment meet. Inspired by the philosophy of "living with light," these contemporary homes feature floor-to-ceiling windows, elegant finishes, and thoughtfully planned layouts. Perfectly positioned with direct access to Sheikh Zayed Road and the Energy Metro, it connects you effortlessly to Dubai Marina, JAFZA, and beyond. Surrounded by retail, green spaces, and lifestyle amenities, Taiyo offers a balanced life—peaceful yet connected. With flexible payment plans and strong rental potential, it\'s not just a home, it\'s a future-ready investment in one of Dubai\'s fastest-growing corridors.',
      features: [
        'Floor-to-ceiling windows',
        'Modern Japanese design',
        'Smart space planning',
        'Premium quality finishes',
        'Prime connectivity location',
        'Pool and gym',
        'Walkable retail access',
        'High rental potential',
      ],
      floorPlans: [
        { type: '1 Bedroom', size: '589 – 650 sq.ft', price: 'AED 850K – 1.3M' },
        { type: '2 Bedroom', size: '1,005 – 1,030 sq.ft', price: 'AED 1.45M – 1.85M' },
        { type: '3 Bedroom', size: '1,376 – 1,603 sq.ft', price: 'AED 2.2M – 2.8M+' },
      ],
      images: [
        'https://res.cloudinary.com/dqfcgy1vv/image/upload/q_auto/f_auto/v1776808654/tyio_2_tykilp.png',
        'https://res.cloudinary.com/dqfcgy1vv/image/upload/q_auto/f_auto/v1776808653/tyio_3_mqudiw.png',
        'https://res.cloudinary.com/dqfcgy1vv/image/upload/q_auto/f_auto/v1776808653/Tyio_1_vt1yyc.png',
      ]
    },
    'damac-islands': {
      name: 'Damac Islands',
      developer: 'DAMAC',
      location: 'Dubailand',
      hero: 'https://res.cloudinary.com/dqfcgy1vv/image/upload/v1776776609/dmaac_island_gv9fyk.png',
      price: 'Starting from AED 2,500,000',
      completion: 'Q4 2028',
      description: 'Damac Islands is a waterfront-inspired townhouse community designed for modern family living with a resort-style touch. Surrounded by lush landscapes, lagoons, and curated leisure spaces, it offers a peaceful escape without disconnecting from Dubai\'s key hubs. Each townhouse blends contemporary architecture with practical layouts, maximizing space, light, and comfort. With Damac\'s signature lifestyle amenities, from beach zones to fitness areas, residents experience everyday living like a holiday. Positioned for strong capital appreciation and rental demand, Damac Islands is not just a home—it\'s a lifestyle investment where tranquility, connectivity, and long-term value come together seamlessly.',
      features: [
        'Lagoon-style living',
        'Modern townhouse layouts',
        'Resort lifestyle amenities',
        'Gated community living',
        'Family-friendly environment',
        'Green open spaces',
        'Prime growth location',
        'High ROI potential',
      ],
      floorPlans: [
        { type: '4BR Townhouse', size: '2,200–2,400 sq.ft', price: 'AED 2.4M – 3.0M' },
        { type: '5BR Townhouse', size: '3,000–3,300 sq.ft', price: 'AED 3.2M – 4.2M' },
      ],
      images: [
        'https://res.cloudinary.com/dqfcgy1vv/image/upload/q_auto/f_auto/v1776808849/dmac_island_ioiook.png',
        'https://res.cloudinary.com/dqfcgy1vv/image/upload/q_auto/f_auto/v1776808850/dmac_island_2_yuumxu.png',
        'https://res.cloudinary.com/dqfcgy1vv/image/upload/q_auto/f_auto/v1776808850/Damac_island_3_uriphz.png',
      ]
    },
    'hillside-residences': {
      name: 'Hillside Residences',
      developer: 'WASL',
      location: 'Wasl Gate',
      hero: 'https://res.cloudinary.com/dqfcgy1vv/image/upload/v1776776609/hillside_fsajqw.png',
      price: 'Starting from AED 1,200,000',
      completion: 'Q3 2027',
      description: 'Hillside Residences offers elevated living where design meets serenity. Nestled in a peaceful setting with sweeping views and landscaped surroundings, the project is crafted for those who value calm, space, and refinement. The architecture focuses on clean lines, natural light, and functional layouts that enhance everyday comfort. With premium amenities and seamless connectivity to Dubai\'s key districts, Hillside balances quiet living with urban convenience. Ideal for both end-users and investors, it presents a strong opportunity for steady rental returns and long-term appreciation. This is a place where lifestyle feels effortless, and every detail quietly works in your favor.',
      features: [
        'Elevated hillside views',
        'Modern sleek design',
        'Bright living spaces',
        'Premium finishing quality',
        'Calm residential setting',
        'Lifestyle amenities included',
        'Strong rental demand',
        'Strategic location access',
      ],
      floorPlans: [
        { type: 'Studio', size: '400–450 sq.ft', price: 'AED 550K – 700K' },
        { type: '1BR', size: '650–750 sq.ft', price: 'AED 850K – 1.2M' },
        { type: '2BR', size: '1,000–1,200 sq.ft', price: 'AED 1.4M – 1.9M' },
      ],
      images: [
        'https://res.cloudinary.com/dqfcgy1vv/image/upload/q_auto/f_auto/v1776809033/Hill_side_2_tpmvoz.png',
        'https://res.cloudinary.com/dqfcgy1vv/image/upload/q_auto/f_auto/v1776809032/Hill_side_3_y21ne8.png',
        'https://res.cloudinary.com/dqfcgy1vv/image/upload/q_auto/f_auto/v1776809031/Hillside_1_cxjoms.png',
      ]
    },
    'emaar-the-heights': {
      name: 'Emaar The Heights',
      developer: 'EMAAR',
      location: 'Dubai South',
      hero: 'https://res.cloudinary.com/dqfcgy1vv/image/upload/v1776776608/the_hieghts_adizsg.png',
      price: 'Starting from AED 6,000,000',
      completion: 'Q3 2029',
      description: 'Emaar The Heights is a master-planned villa community built around space, greenery, and timeless design. Developed by Emaar, it carries the hallmark of quality, planning, and long-term value. Wide streets, landscaped parks, and community-centric living define the experience here. The villas are designed with spacious layouts, large windows, and elegant finishes, making them ideal for families seeking both comfort and prestige. With excellent connectivity and future growth potential, The Heights stands as a smart investment and a peaceful retreat from the city\'s pace. It\'s where classic community living meets modern Dubai ambition.',
      features: [
        'Emaar master community',
        'Spacious villa layouts',
        'Green park living',
        'Premium build quality',
        'Family-centric design',
        'Wide internal roads',
        'High appreciation potential',
        'Trusted developer brand',
      ],
      floorPlans: [
        { type: '3BR Villa', size: '2,100–2,300 sq.ft', price: 'AED 2.7M – 3.5M' },
        { type: '4BR Villa', size: '2,800–3,200 sq.ft', price: 'AED 3.8M – 5.0M' },
        { type: '5BR Villa', size: '4,000+ sq.ft', price: 'AED 5.5M – 7.5M' },
      ],
      images: [
        'https://res.cloudinary.com/dqfcgy1vv/image/upload/q_auto/f_auto/v1776809213/the_hiegts_1_ncoc0q.png',
        'https://res.cloudinary.com/dqfcgy1vv/image/upload/q_auto/f_auto/v1776809214/the_hieghts_3_sm9szv.png',
        'https://res.cloudinary.com/dqfcgy1vv/image/upload/q_auto/f_auto/v1776809215/the_hieghts_2_zwjjvn.png',
      ]
    },
    'south-bay-6': {
      name: 'South Bay 6',
      developer: 'DUBAI SOUTH',
      location: 'Dubai South',
      hero: 'https://res.cloudinary.com/dqfcgy1vv/image/upload/v1776776608/south_bay_6_e9ltyz.png',
      price: 'Starting from AED 5,000,000',
      completion: 'Q3 2027',
      description: 'South Bay 6 is a premium waterfront villa community built around a stunning crystal lagoon, offering a rare blend of luxury and tranquility. Designed for refined living, the villas feature expansive layouts, private outdoor spaces, and elegant architecture. Residents enjoy exclusive access to beachfront-style amenities, jogging tracks, and leisure zones that redefine community living. Strategically located near key highways and Al Maktoum Airport, it ensures both connectivity and future value growth. South Bay 6 is ideal for families and investors looking for a lifestyle-driven property with strong capital appreciation in one of Dubai\'s emerging luxury corridors.',
      features: [
        'Crystal lagoon access',
        'Waterfront villa living',
        'Large private layouts',
        'Luxury community design',
        'Beach-style amenities',
        'Peaceful surroundings',
        'Future growth location',
        'High capital appreciation',
      ],
      floorPlans: [
        { type: '4BR Villa', size: '3,200–3,500 sq.ft', price: 'AED 3.5M – 4.8M' },
        { type: '5BR Villa', size: '4,200–4,800 sq.ft', price: 'AED 5.0M – 6.8M' },
        { type: '6BR Villa', size: '5,500+ sq.ft', price: 'AED 7.0M – 9.5M' },
      ],
      images: [
        'https://res.cloudinary.com/dqfcgy1vv/image/upload/q_auto/f_auto/v1776809454/south_bay_1_pnxier.png',
        'https://res.cloudinary.com/dqfcgy1vv/image/upload/q_auto/f_auto/v1776809453/south_bay_3_cggejc.png',
        'https://res.cloudinary.com/dqfcgy1vv/image/upload/q_auto/f_auto/v1776809452/South_bay_2_zvyt7r.png',
      ]
    },
    'avenue-residences-5': {
      name: 'Avenue Residences 5',
      developer: 'NABNI',
      location: 'Furjan',
      hero: 'https://res.cloudinary.com/dqfcgy1vv/image/upload/v1776776608/5th_avenue_dcovww.png',
      price: 'Starting from AED 1,450,000',
      completion: 'Q1 2026',
      description: 'Avenue Residences 5 delivers contemporary urban living with a focus on elegance, efficiency, and connectivity. Designed for modern lifestyles, the apartments feature sleek interiors, smart layouts, and high-quality finishes that maximize comfort and functionality. Located in a vibrant neighborhood with easy access to key roads, retail, and daily conveniences, it offers both lifestyle and practicality. The project appeals to young professionals, families, and investors seeking steady rental income and long-term value. With its balanced pricing and strong location fundamentals, Avenue Residences 5 stands as a smart, future-ready choice in Dubai\'s evolving residential landscape.',
      features: [
        'Contemporary urban design',
        'Smart compact layouts',
        'Premium interior finishes',
        'Excellent connectivity access',
        'Retail nearby convenience',
        'Investor-friendly pricing',
        'Strong rental demand',
        'Modern lifestyle amenities',
      ],
      floorPlans: [
        { type: 'Studio', size: '380–450 sq.ft', price: 'AED 500K – 650K' },
        { type: '1BR', size: '650–800 sq.ft', price: 'AED 800K – 1.1M' },
        { type: '2BR', size: '1,050–1,300 sq.ft', price: 'AED 1.3M – 1.8M' },
      ],
      images: [
        'https://res.cloudinary.com/dqfcgy1vv/image/upload/q_auto/f_auto/v1776809656/avenue_1_zk9c9k.png',
        'https://res.cloudinary.com/dqfcgy1vv/image/upload/q_auto/f_auto/v1776809658/avenue_2_mc1xou.png',
        'https://res.cloudinary.com/dqfcgy1vv/image/upload/q_auto/f_auto/v1776809656/avenue_3_xazqiz.png',
      ]
    },
  };

  const project = projectDetails[projectId] || projectDetails['taiyo-residences'];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50">
      <Header />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0">
          <img src={project.hero} alt={project.name} className="w-full h-full object-cover opacity-20" />
          <div className="absolute inset-0 bg-gradient-to-b from-white/90 via-white/70 to-white" />
        </div>

        <div className="container mx-auto px-6 relative z-10">
          <Link to="/" className="inline-flex items-center gap-2 text-yellow-600 hover:text-yellow-700 font-semibold mb-8 transition-colors">
            <ArrowLeft className="w-5 h-5" />
            <span>Back to Home</span>
          </Link>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="inline-block px-6 py-2 bg-yellow-400 rounded-full text-gray-900 font-bold mb-6">
              {project.developer}
            </div>
            <h1 className="text-5xl md:text-7xl font-bold text-gray-800 mb-4">{project.name}</h1>
            <div className="flex flex-wrap gap-6 text-xl text-gray-700 mb-8">
              <div className="flex items-center gap-2">
                <MapPin className="w-6 h-6 text-yellow-600" />
                <span>{project.location}</span>
              </div>
              <div className="flex items-center gap-2">
                <DollarSign className="w-6 h-6 text-yellow-600" />
                <span>{project.price}</span>
              </div>
              <div className="flex items-center gap-2">
                <Calendar className="w-6 h-6 text-yellow-600" />
                <span>{project.completion}</span>
              </div>
            </div>
            <p className="text-xl text-gray-700 leading-relaxed max-w-4xl">{project.description}</p>
          </motion.div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <h2 className="text-4xl font-bold text-gray-800 mb-12 text-center">Premium Features</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {project.features.map((feature, index) => (
              <motion.div
                key={index}
                className="flex items-start gap-3 p-6 bg-gradient-to-br from-yellow-50 to-white rounded-xl border border-yellow-100"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
              >
                <CheckCircle2 className="w-6 h-6 text-yellow-600 flex-shrink-0 mt-1" />
                <span className="text-gray-700">{feature}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Floor Plans */}
      <section className="py-20">
        <div className="container mx-auto px-6">
          <h2 className="text-4xl font-bold text-gray-800 mb-12 text-center">Floor Plans & Pricing</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {project.floorPlans.map((plan, index) => (
              <motion.div
                key={index}
                className="p-8 bg-white rounded-2xl border border-gray-200 shadow-md hover:shadow-xl transition-all duration-300"
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ y: -8 }}
              >
                <div className="w-16 h-16 bg-gradient-to-br from-yellow-400 to-yellow-600 rounded-xl flex items-center justify-center mb-6">
                  <Building2 className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-gray-800 mb-2">{plan.type}</h3>
                <p className="text-gray-600 mb-4">{plan.size}</p>
                <p className="text-2xl font-bold text-yellow-600">{plan.price}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Image Gallery */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <h2 className="text-4xl font-bold text-gray-800 mb-12 text-center">Project Gallery</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {project.images.map((image, index) => (
              <motion.div
                key={index}
                className="relative h-80 rounded-2xl overflow-hidden shadow-lg"
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ scale: 1.05 }}
              >
                <img src={image} alt={`${project.name} ${index + 1}`} className="w-full h-full object-cover" />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-20 bg-gradient-to-br from-yellow-50 to-white">
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-6">
            Interested in {project.name}?
          </h2>
          <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
            Get detailed floor plans, payment options, and schedule a site visit with our property experts.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <motion.button
              onClick={() => {
                setPopupType('brochure');
                setShowPopup(true);
              }}
              className="inline-flex items-center gap-3 px-10 py-5 bg-gradient-to-r from-yellow-400 to-yellow-600 text-gray-900 font-bold text-lg rounded-full shadow-xl hover:shadow-2xl transition-all duration-300"
              whileHover={{ scale: 1.05, y: -3 }}
              whileTap={{ scale: 0.98 }}
            >
              <Download className="w-5 h-5" />
              <span>Download Brochure</span>
            </motion.button>
            <motion.button
              onClick={() => {
                setPopupType('inventory');
                setShowPopup(true);
              }}
              className="inline-flex items-center gap-3 px-10 py-5 border-2 border-yellow-500 text-yellow-700 font-bold text-lg rounded-full hover:bg-yellow-50 transition-all duration-300"
              whileHover={{ scale: 1.05, y: -3 }}
              whileTap={{ scale: 0.98 }}
            >
              <span>Request More Information</span>
            </motion.button>
          </div>
        </div>
      </section>

      <ContactFormPopup 
        isOpen={showPopup} 
        onClose={() => setShowPopup(false)} 
        formType={popupType}
      />
      <Footer />
    </div>
  );
};

export default ProjectDetail;
